/**
 * AgreementReturn — DocuSign embedded-signing return landing.
 *
 * DocuSign redirects the seller here after they finish (or decline/cancel) the
 * embedded signing session for the listing agreement. The route lives at
 *
 *     /seller/listings/:houseId/agreement-return?document_id=<id>&event=<advisory>
 *
 * On mount it calls `POST /api/listing-agreements/reconcile`, which reads the
 * live envelope status from DocuSign and finalizes the document if completed.
 * This is the local-dev completion path (no public webhook). In production the
 * Connect webhook may have already completed the document; reconcile is
 * idempotent, so either order resolves to one terminal state.
 *
 * States rendered (driven by the reconcile status payload):
 *   - signed        → success card + download link, "Review & publish" (opens
 *                     the draft at the wizard's last step) and "Back to my
 *                     listings"
 *   - in_progress
 *     / sent        → "Your signature is being recorded…" + Refresh/poll button
 *                     (short auto-poll, then stops)
 *   - voided
 *     / declined    → error card + "Sign again" (routes back into the wizard)
 *
 * The `event` query param DocuSign appends (signing_complete / decline /
 * cancel / ttl_expired) is advisory only — the authoritative state always
 * comes from the reconcile response.
 *
 * Routing
 * -------
 * The cutthe6.com frontend does not use React Router; App.js inspects
 * `window.location.pathname` and renders this component (inside the auth gate,
 * since reconcile requires the authenticated seller) when the path matches
 * `/seller/listings/:houseId/agreement-return`.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import APIAgent from '../../agents/apiAgent';
import { resolveDownloadUrl } from '../../utils/downloadUrl';
import './AgreementReturn.css';

// Auto-poll cadence while the signature is still being recorded. We poll a few
// times then stop and leave the seller a manual Refresh affordance. Reconcile
// reads the live envelope status, so this resolves locally without a webhook.
const POLL_INTERVAL_MS = 5000;
const MAX_AUTO_POLLS = 6; // ~30s of background polling

const formatUtcDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
};

/**
 * Parse the return URL for the house id (path segment) and document id (query).
 * Exported for unit testing.
 */
export function parseReturnParams(
  pathname = typeof window !== 'undefined' ? window.location.pathname : '',
  search = typeof window !== 'undefined' ? window.location.search : '',
) {
  const match = /\/seller\/listings\/(\d+)\/agreement-return/.exec(pathname || '');
  const houseId = match ? Number(match[1]) : null;
  const params = new URLSearchParams(search || '');
  const rawDoc = params.get('document_id');
  const rawDocNum = rawDoc != null && rawDoc !== '' ? Number(rawDoc) : NaN;
  const documentId = Number.isFinite(rawDocNum) ? rawDocNum : null;
  const event = params.get('event') || null;
  return { houseId, documentId, event };
}

/**
 * @param {object} props
 * @param {number} [props.houseId] - override (defaults to URL path segment)
 * @param {number} [props.documentId] - override (defaults to URL query param)
 * @param {() => void} [props.onDone] - called with the signed house id to return
 *   the seller to their listings; falls back to a hard nav to `/`.
 */
export default function AgreementReturn({
  houseId: houseIdProp,
  documentId: documentIdProp,
  onDone,
  // Opens the signed draft straight at Review & Publish. Without it the seller
  // had to find the draft, reopen it and click Continue through every step.
  onReviewAndPublish,
} = {}) {
  const parsed = useRef(parseReturnParams()).current;
  const houseId = houseIdProp ?? parsed.houseId;
  const documentId = documentIdProp ?? parsed.documentId;

  // Lifecycle: 'reconciling' | 'signed' | 'in_progress' | 'voided' | 'error'
  const [phase, setPhase] = useState('reconciling');
  const [status, setStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [pollCount, setPollCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const headingRef = useRef(null);
  const cancelledRef = useRef(false);

  // Map a reconcile status payload onto a UI phase.
  const applyStatus = useCallback((payload) => {
    const next = payload?.status || (payload?.has_signed_agreement ? 'signed' : 'in_progress');
    setStatus(payload);
    if (next === 'signed') {
      setPhase('signed');
    } else if (next === 'voided' || next === 'declined') {
      setPhase('voided');
    } else if (next === 'none') {
      // No agreement document exists for this seller+house. This is terminal —
      // there is nothing to finalize, so surface a "not found" error rather than
      // auto-polling a document that will never complete.
      setPhase('error');
      setErrorMessage("We couldn't find a signature in progress for this listing.");
    } else {
      // 'sent' | 'in_progress' → still finalizing.
      setPhase('in_progress');
    }
  }, []);

  const runReconcile = useCallback(async () => {
    if (!houseId) {
      setPhase('error');
      setErrorMessage('We could not determine which listing this signature belongs to.');
      return;
    }
    try {
      const payload = await APIAgent.reconcileListingAgreement(houseId, documentId);
      if (cancelledRef.current) return;
      applyStatus(payload);
    } catch (err) {
      if (cancelledRef.current) return;
      console.error('Listing agreement reconcile failed:', err);
      setPhase('error');
      setErrorMessage(
        err?.message || "We couldn't confirm your signature. Please try again.",
      );
    }
  }, [houseId, documentId, applyStatus]);

  // Reconcile on mount.
  useEffect(() => {
    cancelledRef.current = false;
    runReconcile();
    return () => {
      cancelledRef.current = true;
    };
  }, [runReconcile]);

  // Short auto-poll while finalizing. Stops after MAX_AUTO_POLLS or once a
  // terminal state is reached.
  useEffect(() => {
    if (phase !== 'in_progress') return undefined;
    if (pollCount >= MAX_AUTO_POLLS) return undefined;
    const id = setTimeout(() => {
      setPollCount((c) => c + 1);
      runReconcile();
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(id);
  }, [phase, pollCount, runReconcile]);

  // Focus the heading when the state settles so AT users land on the result.
  useEffect(() => {
    if ((phase === 'signed' || phase === 'voided' || phase === 'error') && headingRef.current) {
      headingRef.current.focus();
    }
  }, [phase]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await runReconcile();
    setRefreshing(false);
  };

  const handleBackToListings = () => {
    if (typeof onDone === 'function') {
      onDone(houseId);
      return;
    }
    window.location.assign('/');
  };

  const handleSignAgain = () => {
    // Route back into the wizard's sign step. Without React Router we navigate
    // to the seller listings tab; the wizard re-fetches status and surfaces the
    // "Sign again" / re-initiate affordance for the voided envelope.
    if (typeof onDone === 'function') {
      onDone(houseId);
      return;
    }
    window.location.assign('/');
  };

  const downloadUrl = status?.download_url ? resolveDownloadUrl(status.download_url) : '';

  return (
    <div className="agreement-return" data-testid="agreement-return">
      <div
        className="agreement-return__card"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {phase === 'reconciling' && (
          <div className="agreement-return__pending" data-testid="agreement-return-reconciling">
            <span className="agreement-return__spinner" aria-hidden="true" />
            <h1 className="agreement-return__title">Confirming your signature…</h1>
            <p className="agreement-return__subtitle">
              Just a moment while we finalize your listing agreement with DocuSign.
            </p>
          </div>
        )}

        {phase === 'signed' && (
          <div data-testid="agreement-return-signed">
            <h1
              className="agreement-return__title agreement-return__title--success"
              ref={headingRef}
              tabIndex={-1}
            >
              <span className="agreement-return__icon" aria-hidden="true">✓</span>
              Listing agreement signed
            </h1>
            <p className="agreement-return__subtitle">
              Your signature has been recorded. You can now publish your listing.
            </p>
            <dl className="agreement-return__meta">
              {status?.signer_name && (
                <div>
                  <dt>Signed by</dt>
                  <dd data-testid="agreement-return-signer">{status.signer_name}</dd>
                </div>
              )}
              {status?.signed_at && (
                <div>
                  <dt>When</dt>
                  <dd data-testid="agreement-return-signed-at">{formatUtcDate(status.signed_at)}</dd>
                </div>
              )}
              {status?.template_version != null && (
                <div>
                  <dt>Template</dt>
                  <dd>v{status.template_version}</dd>
                </div>
              )}
            </dl>
            <div className="agreement-return__actions">
              {downloadUrl && (
                <a
                  className="agreement-return__btn agreement-return__btn--secondary"
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="agreement-return-download"
                >
                  Download signed copy (PDF)
                </a>
              )}
              {typeof onReviewAndPublish === 'function' && houseId != null && (
                <button
                  type="button"
                  className="agreement-return__btn agreement-return__btn--primary"
                  onClick={() => onReviewAndPublish(houseId)}
                  data-testid="agreement-return-review"
                >
                  Review &amp; publish
                </button>
              )}
              <button
                type="button"
                className={`agreement-return__btn ${
                  typeof onReviewAndPublish === 'function' && houseId != null
                    ? 'agreement-return__btn--secondary'
                    : 'agreement-return__btn--primary'
                }`}
                onClick={handleBackToListings}
                data-testid="agreement-return-back"
              >
                Back to my listings
              </button>
            </div>
          </div>
        )}

        {phase === 'in_progress' && (
          <div data-testid="agreement-return-in-progress">
            <h1 className="agreement-return__title">
              <span className="agreement-return__spinner agreement-return__spinner--inline" aria-hidden="true" />
              Your signature is being recorded…
            </h1>
            <p className="agreement-return__subtitle">
              DocuSign is finalizing your agreement. This usually only takes a few
              seconds. We&apos;ll keep checking automatically.
            </p>
            <div className="agreement-return__actions">
              <button
                type="button"
                className="agreement-return__btn agreement-return__btn--primary"
                onClick={handleManualRefresh}
                disabled={refreshing}
                data-testid="agreement-return-refresh"
              >
                {refreshing ? 'Checking…' : 'Refresh status'}
              </button>
              <button
                type="button"
                className="agreement-return__btn agreement-return__btn--ghost"
                onClick={handleBackToListings}
                data-testid="agreement-return-back"
              >
                Back to my listings
              </button>
            </div>
            {pollCount >= MAX_AUTO_POLLS && (
              <p className="agreement-return__hint" data-testid="agreement-return-poll-stopped">
                Still finalizing. You can safely leave this page — your listing
                will update automatically once DocuSign confirms. Use Refresh to
                check again now.
              </p>
            )}
          </div>
        )}

        {phase === 'voided' && (
          <div data-testid="agreement-return-voided">
            <h1
              className="agreement-return__title agreement-return__title--error"
              ref={headingRef}
              tabIndex={-1}
            >
              <span className="agreement-return__icon" aria-hidden="true">⚠</span>
              Signing was not completed
            </h1>
            <p className="agreement-return__subtitle">
              The agreement was declined or cancelled, so nothing was signed. You
              can start the signing process again whenever you&apos;re ready.
            </p>
            <div className="agreement-return__actions">
              <button
                type="button"
                className="agreement-return__btn agreement-return__btn--primary"
                onClick={handleSignAgain}
                data-testid="agreement-return-sign-again"
              >
                Sign again
              </button>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div data-testid="agreement-return-error">
            <h1
              className="agreement-return__title agreement-return__title--error"
              ref={headingRef}
              tabIndex={-1}
            >
              <span className="agreement-return__icon" aria-hidden="true">⚠</span>
              Something went wrong
            </h1>
            <p className="agreement-return__subtitle" role="alert">
              {errorMessage}
            </p>
            <div className="agreement-return__actions">
              <button
                type="button"
                className="agreement-return__btn agreement-return__btn--primary"
                onClick={handleManualRefresh}
                disabled={refreshing}
                data-testid="agreement-return-retry"
              >
                {refreshing ? 'Retrying…' : 'Try again'}
              </button>
              <button
                type="button"
                className="agreement-return__btn agreement-return__btn--ghost"
                onClick={handleBackToListings}
                data-testid="agreement-return-back"
              >
                Back to my listings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
